import React, { useEffect, useRef, useState } from 'react';
import {
  Column,
  Heading,
  Stack,
  SvgCaretDown,
  SvgCaretUp,
} from '@grubhubprod/gh-cookbook-react';
import {
  Button,
  Copy,
  Flex,
  Row,
  Square,
  Squish,
} from '@grubhubprod/gh-cookbook-react/dist';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import {
  IInvoice,
  ISearchParams,
  SearchStatus,
  useInvoiceSearch,
} from 'src/utilities/useSearchHooks';
import Table from 'src/components/Table/Table';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { sdk } from 'src/utilities/sdk';
import { pushError, pushErrorFromAPI } from 'src/utilities/notifications';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import { MediaTypes, saveFile } from 'src/utilities/saveFile';
import { Pagination } from 'src/components/Pagination/Pagination';
import { getHomeBreadcrumb } from 'src/constants/urls';
import {
  getAmountOwed,
  getInvoiceFileName,
  getInvoiceTransactionsFallback,
  getStatusMessage,
  getTransactionsCSVFileName,
  invoiceDueDateFormat,
  InvoiceState,
  toUTCDateFormat,
  TransactionFormat,
} from 'src/components/EnterpriseInvoices/enterpriseInvoices.utils';
import {
  currencyFormatter,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { ICreditCard } from 'src/types/CreditCard';
import { EnterpriseConfirmPaymentModal } from 'src/components/EnterpriseInvoices/EnterpriseConfirmPaymentModal';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { RouterLink } from 'src/components/RouterLink/RouterLink';

export default function EnterpriseInvoices(): JSX.Element {
  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();
  const [card, setCard] = useState<ICreditCard>(null);
  const [invoiceForPaymentModal, setInvoiceForPaymentModal] =
    useState<IInvoice>(null);
  const [isCardCheckComplete, setIsCardCheckComplete] =
    useState<boolean>(false);
  const isAnalyticsSent = useRef(false);
  const {
    corpData,
    enterpriseData: {
      isFinancialAdminUser,
      isInternalAdminUser,
      isParentAdminUser,
    },
    corpId,
  } = useAuth();
  const isCreditCardPrimary =
    !!corpData && corpData.invoicePaymentMethod === 'CREDIT_CARD_PRIMARY';
  const invoicingMethod = corpData ? corpData.invoicingMethod : '';
  const canAccessCreditCard =
    isInternalAdminUser || isFinancialAdminUser || isParentAdminUser;
  const useNarrowColumn =
    (isCreditCardPrimary && canAccessCreditCard) ||
    invoicingMethod === 'MULTI_DEPARTMENT' ||
    invoicingMethod === 'MULTI_LOCATION';

  const {
    doSearch,
    resultsList: invoices,
    currentSearchPage,
    searchStatus,
    totalPages,
    totalResults,
    hasNoResultsWithoutQuery,
  } = useInvoiceSearch(corpId);

  useEffect(() => {
    if (corpId) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    if (corpId && isCreditCardPrimary && canAccessCreditCard) {
      getCreditCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId, isCreditCardPrimary, canAccessCreditCard]);

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin',
      pagesubgroup: 'invoices',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !isAnalyticsSent.current &&
      searchStatus === SearchStatus.COMPLETE &&
      totalResults > 0
    ) {
      sendTealiumEvent({
        eventcategory: 'invoices interaction',
        eventaction: 'invoices available impression',
        eventlabel: '',
      });
      isAnalyticsSent.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStatus, totalResults, isAnalyticsSent.current]);

  async function getCreditCard() {
    try {
      let paymentApiClientTokenUrl = `/payments/corporate/${corpId}/credit_card`;

      if (isInternalAdminUser) {
        paymentApiClientTokenUrl = `${paymentApiClientTokenUrl}/internal_admin`;
      }

      const [card] = await sdk.http.get(paymentApiClientTokenUrl);
      setCard(card);
      setIsCardCheckComplete(true);
    } catch (error) {
      pushErrorFromAPI(error);
      setIsCardCheckComplete(true);
    }
  }

  async function getInvoiceTransactions(
    invoice: IInvoice,
    mediaType: MediaTypes,
    detailed = false
  ): Promise<void> {
    try {
      const fileData = await getFileData(corpId, invoice, mediaType, detailed);

      const filename = invoice.invoice_number
        ? getInvoiceFileName(mediaType, invoice.invoice_number)
        : getTransactionsCSVFileName(invoice);

      saveFile(fileData, filename, mediaType);
      sendTealiumEvent({
        eventcategory: 'invoices interaction',
        eventaction: getAnalyticsEventAction(mediaType, detailed),
        eventlabel: 'success',
      });
    } catch (error) {
      if (error.statusCode === 404 || error.statusCode === 403) {
        pushError(`A system error has occurred. Please try downloading
                        the file again or contact your account manager.`);
      } else {
        pushErrorFromAPI(error);
      }
    }
  }

  async function getFileData(
    corpId: string,
    invoice: IInvoice,
    mediaType: string,
    detailed?: boolean
  ): Promise<string | ArrayBuffer> {
    let downloadEndPoint;

    if (mediaType === MediaTypes.TEXT_CSV) {
      if (invoice.csv_url) {
        const response = await fetch(invoice.csv_url);
        return response.text();
      }
      downloadEndPoint = `/corps/${corpId}/invoice/${invoice.invoice_id}/transactions/${TransactionFormat.CORP_ADMIN}`;

      return getInvoiceTransactionsFallback(downloadEndPoint);
    }
    if (mediaType === MediaTypes.APPLICATION_PDF) {
      if (detailed) {
        const response = await fetch(invoice.detailed_pdf_url);
        return response.arrayBuffer();
      }
      if (invoice.pdf_url) {
        const response = await fetch(invoice.pdf_url);
        return response.arrayBuffer();
      }
      downloadEndPoint = `/corps/${corpId}/invoice/${invoice.invoice_id}/pdf`;

      return getInvoiceTransactionsFallback(downloadEndPoint, true);
    }
    return null;
  }

  function getAnalyticsEventAction(
    mediaType: MediaTypes,
    detailed: boolean
  ): string {
    switch (mediaType) {
      case MediaTypes.TEXT_CSV:
        return 'csv download_cta';
      case MediaTypes.APPLICATION_PDF:
        return detailed ? 'details download_cta' : 'summary download_cta';
      default:
        return '';
    }
  }

  return (
    <Square inset="md">
      <PageHeader>
        <Stack y="xxx-sm">
          <Heading variant="h1">Invoices</Heading>
        </Stack>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpId),
            },
          ]}
        />
      </PageHeader>
      {isCardCheckComplete &&
        !card &&
        isCreditCardPrimary &&
        (isFinancialAdminUser || isParentAdminUser) && (
          <Stack y="lg">
            Please{' '}
            <RouterLink href={`/corporate/enterprise/${corpId}/payments`}>
              add a credit card
            </RouterLink>{' '}
            so you can make invoice payments
          </Stack>
        )}
      <Stack y="lg">
        <PageSection>
          <Stack y="md">
            <Heading variant="h4">Invoices</Heading>
          </Stack>
          <Stack y={totalPages > 1 ? 'md' : 'none'}>
            <Table
              isOverflowVisible
              isLoading={searchStatus === SearchStatus.LOADING}
              header={() => (
                <Row>
                  <Column phone={2}>
                    <Copy variant="body" color="secondaryText">
                      Invoice Number
                    </Copy>
                  </Column>
                  <Column phone={2}>
                    <Copy variant="body" color="secondaryText">
                      Invoice Period
                    </Copy>
                  </Column>
                  <Column phone={useNarrowColumn ? 1 : 2}>
                    <Copy variant="body" color="secondaryText">
                      Total
                    </Copy>
                  </Column>
                  <Column phone={useNarrowColumn ? 1 : 2}>
                    <Copy variant="body" color="secondaryText">
                      You Owe
                    </Copy>
                  </Column>
                  <Column phone={useNarrowColumn ? 1 : 2}>
                    <Copy variant="body" color="secondaryText">
                      Due Date
                    </Copy>
                  </Column>
                  {isCreditCardPrimary && canAccessCreditCard && (
                    <Column gutter={24} phone={2}>
                      <Copy variant="body" color="secondaryText">
                        Status
                      </Copy>
                    </Column>
                  )}

                  {(invoicingMethod === 'MULTI_DEPARTMENT' ||
                    invoicingMethod === 'MULTI_LOCATION') && (
                    <Column
                      phone={isCreditCardPrimary && canAccessCreditCard ? 1 : 2}
                    >
                      {invoicingMethod === 'MULTI_DEPARTMENT' && (
                        <Copy variant="body" color="secondaryText">
                          Departments
                        </Copy>
                      )}
                      {invoicingMethod === 'MULTI_LOCATION' && (
                        <Copy variant="body" color="secondaryText">
                          Office Location
                        </Copy>
                      )}
                    </Column>
                  )}
                  <Column gutter={24} phone={1}>
                    <Copy variant="body" color="secondaryText">
                      Download
                    </Copy>
                  </Column>
                </Row>
              )}
              body={() =>
                totalResults > 0 ? (
                  <ul className="removeListStyles">
                    {invoices.map((invoice) => (
                      <Square
                        as="li"
                        data-testid={`invoices-row-${invoice.invoice_id}`}
                        key={invoice.invoice_id}
                        inset="md"
                        borderBottom="1px solid"
                        borderColor="lightLine"
                      >
                        <Row>
                          <Column phone={2} className="centeredFlexColumn">
                            {invoice.invoice_number}
                          </Column>
                          <Column phone={2} className="centeredFlexColumn">
                            {toUTCDateFormat(invoice.start)}
                            {' – '}
                            {toUTCDateFormat(invoice.end)}
                          </Column>
                          <Column
                            phone={useNarrowColumn ? 1 : 2}
                            className="centeredFlexColumn"
                          >
                            {currencyFormatter(
                              Number(
                                transformPenniesToDollar(invoice.invoice_amount)
                              )
                            )}
                          </Column>
                          <Column
                            phone={useNarrowColumn ? 1 : 2}
                            className="centeredFlexColumn"
                          >
                            {currencyFormatter(
                              Number(
                                transformPenniesToDollar(
                                  getAmountOwed(
                                    invoice.amount_owed,
                                    invoice.state
                                  )
                                )
                              )
                            )}
                          </Column>
                          <Column
                            phone={useNarrowColumn ? 1 : 2}
                            className="centeredFlexColumn"
                          >
                            {invoiceDueDateFormat(invoice.due_date)}
                          </Column>
                          {isCreditCardPrimary && canAccessCreditCard && (
                            <Column
                              gutter={24}
                              phone={2}
                              className="centeredFlexColumn"
                            >
                              {(invoice.state === InvoiceState.UNPAID ||
                                invoice.state ===
                                  InvoiceState.CC_PAYMENT_FAILED) && (
                                <Button
                                  buttonSize="small"
                                  disabled={
                                    isInternalAdminUser ||
                                    invoice.eligible_for_ccpayment === false ||
                                    !card
                                  }
                                  onClick={() =>
                                    setInvoiceForPaymentModal(invoice)
                                  }
                                >
                                  Pay
                                </Button>
                              )}
                              {getStatusMessage(
                                invoice,
                                ({ message, color = 'secondaryText' }) => {
                                  return (
                                    message && (
                                      <Copy variant="body" color={color}>
                                        {message}
                                      </Copy>
                                    )
                                  );
                                }
                              )}
                            </Column>
                          )}
                          {(invoicingMethod === 'MULTI_DEPARTMENT' ||
                            invoicingMethod === 'MULTI_LOCATION') && (
                            <Column
                              phone={
                                isCreditCardPrimary && canAccessCreditCard
                                  ? 1
                                  : 2
                              }
                              className="centeredFlexColumn"
                            >
                              {invoice.invoiced_entity_name || 'N/A'}
                            </Column>
                          )}

                          <Column gutter={24} phone={2}>
                            <Flex
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <DropdownPdf
                                invoice={invoice}
                                getInvoiceTransactionsDetailed={() =>
                                  getInvoiceTransactions(
                                    invoice,
                                    MediaTypes.APPLICATION_PDF,
                                    true
                                  )
                                }
                                getInvoiceTransactionsSummary={() =>
                                  getInvoiceTransactions(
                                    invoice,
                                    MediaTypes.APPLICATION_PDF,
                                    false
                                  )
                                }
                              />
                              <Button
                                variant="tertiary"
                                buttonSize="small"
                                onClick={() =>
                                  getInvoiceTransactions(
                                    invoice,
                                    MediaTypes.TEXT_CSV
                                  )
                                }
                              >
                                CSV
                              </Button>
                            </Flex>
                          </Column>
                        </Row>
                      </Square>
                    ))}
                  </ul>
                ) : (
                  hasNoResultsWithoutQuery && (
                    <NoResultsMsg
                      color="secondaryText"
                      message="You currently have no invoices."
                    />
                  )
                )
              }
              showFooter={false}
            />
          </Stack>
          {totalPages > 1 && (
            <Flex justifyContent="center">
              <Pagination
                boundaryLinks
                page={currentSearchPage}
                collectionSize={totalResults}
                pageChange={(page) => {
                  const searchParams: ISearchParams = { page };
                  doSearch(searchParams);
                }}
              />
            </Flex>
          )}
          {invoiceForPaymentModal && (
            <EnterpriseConfirmPaymentModal
              invoice={invoiceForPaymentModal}
              card={card}
              onSuccess={doSearch}
              onClose={() => setInvoiceForPaymentModal(null)}
              corpId={corpId}
            />
          )}
        </PageSection>
      </Stack>
    </Square>
  );
}

interface DropdownPdfProps {
  invoice: IInvoice;
  getInvoiceTransactionsSummary: () => Promise<void>;
  getInvoiceTransactionsDetailed: () => Promise<void>;
}

export function DropdownPdf({
  invoice,
  getInvoiceTransactionsSummary,
  getInvoiceTransactionsDetailed,
}: DropdownPdfProps): JSX.Element {
  return (
    <>
      {invoice.detailed_pdf_url ? (
        <Dropdown
          isMenuCentered
          closeMenuOnClick={false}
          toggle={({ expanded }) => (
            <Button variant="tertiary" buttonSize="small">
              <Stack as="span" x="xxx-sm">
                PDF
              </Stack>
              <Copy variant="body" color="interactive">
                {expanded ? (
                  <SvgCaretUp size="16px" pointerEvents="none" />
                ) : (
                  <SvgCaretDown size="16px" pointerEvents="none" />
                )}
              </Copy>
            </Button>
          )}
          menu={({ show }) =>
            show && (
              <Flex
                position="relative"
                flexDirection="column"
                width="120px"
                dimension="1"
                zIndex={10}
                bg="globals.white"
              >
                <Squish
                  inset="sm"
                  bg="globals.white"
                  display="block"
                  borderBottomStyle="solid"
                  borderBottomColor="lightLine"
                  borderBottomWidth={1}
                >
                  <Button
                    variant="tertiary"
                    buttonSize="small"
                    block
                    onClick={getInvoiceTransactionsSummary}
                  >
                    <Copy
                      variant="body"
                      textAlign="right"
                      color="interactive"
                      display="block"
                    >
                      Summary
                    </Copy>
                  </Button>
                </Squish>
                <Squish
                  inset="sm"
                  bg="globals.white"
                  display="block"
                  borderBottomStyle="solid"
                  borderBottomColor="lightLine"
                  borderBottomWidth={1}
                >
                  <Button
                    variant="tertiary"
                    buttonSize="small"
                    block
                    onClick={getInvoiceTransactionsDetailed}
                  >
                    <Copy
                      variant="body"
                      textAlign="right"
                      color="interactive"
                      display="block"
                    >
                      Details
                    </Copy>
                  </Button>
                </Squish>
              </Flex>
            )
          }
        />
      ) : (
        <Button
          variant="tertiary"
          buttonSize="small"
          onClick={getInvoiceTransactionsSummary}
        >
          PDF
        </Button>
      )}
    </>
  );
}
